import type {
  ChecklistItem,
  ChecklistSectionConfig,
  TranslatedText,
} from "./checklist-config.ts";

// Escape special characters for use inside HTML attribute values
function esc(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function requiredBadge(required: TranslatedText): string {
  const norm = required.en.trim().toLowerCase();
  let cls = "bg-slate-100 text-slate-600";
  if (norm === "required") cls = "bg-red-100 text-red-700";
  else if (norm === "recommended") cls = "bg-amber-100 text-amber-700";
  else if (norm === "optional") cls = "bg-green-100 text-green-700";
  return `<span class="inline-block text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cls}" data-en="${
    esc(required.en)
  }" data-lv="${esc(required.lv)}" data-it="${esc(required.it)}">${
    esc(required.en)
  }</span>`;
}

class ChecklistSection extends HTMLElement {
  private config: ChecklistSectionConfig | null = null;

  setConfig(config: ChecklistSectionConfig) {
    this.config = config;
    this.render();
  }

  private renderItemRow(item: ChecklistItem, idx: number): string {
    return `
      <tr class="border-b border-slate-100 hover:bg-slate-50 align-top">
        <td class="p-3 w-8">
          <input type="checkbox" class="w-4 h-4 text-indigo-600 rounded border-slate-300 mt-0.5">
        </td>
        <td class="p-3 font-medium text-slate-900">
          <span data-en="${esc(item.requirement.en)}" data-lv="${
      esc(item.requirement.lv)
    }" data-it="${esc(item.requirement.it)}">${esc(item.requirement.en)}</span>
        </td>
        <td class="p-3 w-28 text-center">${requiredBadge(item.required)}</td>
        <td class="p-3 text-slate-600 text-sm">
          <span data-en="${esc(item.details.en)}" data-lv="${
      esc(item.details.lv)
    }" data-it="${esc(item.details.it)}">${esc(item.details.en)}</span>
        </td>
        <td class="p-3 text-slate-500 text-xs leading-relaxed w-64">
          <span data-en="${esc(item.description.en)}" data-lv="${
      esc(item.description.lv)
    }" data-it="${esc(item.description.it)}">${esc(item.description.en)}</span>
        </td>
      </tr>`;
  }

  private renderSubsections(): string {
    if (!this.config) return "";
    return this.config.subsections.map((sub) => `
      <div class="mb-6">
        <h4 class="font-bold text-slate-900 mb-3 flex items-center gap-2">
          <span class="w-2 h-5 rounded bg-indigo-500 inline-block"></span>
          <span data-en="${esc(sub.title.en)}" data-lv="${
      esc(sub.title.lv)
    }" data-it="${esc(sub.title.it)}">${esc(sub.title.en)}</span>
        </h4>
        ${
      sub.description
        ? `
        <p class="text-slate-500 text-sm mb-3 pl-4">
          <span data-en="${esc(sub.description.en)}" data-lv="${
          esc(sub.description.lv)
        }" data-it="${esc(sub.description.it)}">${
          esc(sub.description.en)
        }</span>
        </p>`
        : ""
    }
        <div class="overflow-x-auto rounded-lg border border-slate-200">
          <table class="w-full text-sm" style="min-width:600px">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th class="p-3 w-8"></th>
                <th class="p-3 text-left font-semibold">Task Requirement</th>
                <th class="p-3 text-center font-semibold w-28">Required</th>
                <th class="p-3 text-left font-semibold">Details</th>
                <th class="p-3 text-left font-semibold w-64">Full Description &amp; Examples</th>
              </tr>
            </thead>
            <tbody>
              ${
      sub.items.map((item, i) => this.renderItemRow(item, i)).join("")
    }
            </tbody>
          </table>
        </div>
      </div>`).join("");
  }

  private render() {
    if (!this.config) return;
    const { gradient, title, tagline } = this.config;

    this.innerHTML = `
      <details class="group bg-gradient-to-r ${gradient} rounded-xl overflow-hidden mb-4">
        <summary class="cursor-pointer p-6 text-white font-bold text-lg flex items-center justify-between hover:brightness-110 transition-all">
          <span class="flex items-center gap-3">
            <input type="checkbox" class="w-5 h-5 text-indigo-600 rounded border-white bg-white" onclick="event.stopPropagation()">
            <span data-en="${esc(title.en)}" data-lv="${
      esc(title.lv)
    }" data-it="${esc(title.it)}">${esc(title.en)}</span>
          </span>
          <svg class="w-6 h-6 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </summary>
        <div class="bg-white p-6 space-y-6">
          <p class="text-slate-600 italic mb-4">
            <span data-en="${esc(tagline.en)}" data-lv="${
      esc(tagline.lv)
    }" data-it="${esc(tagline.it)}">${esc(tagline.en)}</span>
          </p>
          ${this.renderSubsections()}
        </div>
      </details>
    `;

    // Re-apply active language after rendering
    if (globalThis.switchLang) {
      const currentLang = localStorage.getItem("dammi_lang") || "en";
      globalThis.switchLang(currentLang);
    }
  }
}

customElements.define("checklist-section", ChecklistSection);

export { ChecklistSection };
